/**
 * Bio Image Modal Component
 * Shows an image panel to the right of the 3D model with image and description
 */
export class BioImageModal {
    constructor() {
        this.modalContainer = null;
        this.modalImage = null;
        this.modalDescription = null;
        this.closeButton = null;

        this.init();
    }

    /**
     * Initialize the image panel
     */
    init() {
        // Create modal container if it doesn't exist
        if (!document.getElementById("bio-image-panel")) {
            this.createPanelStructure();
        } else {
            // Get references to existing panel elements
            this.modalContainer = document.getElementById("bio-image-panel");
            this.modalImage = this.modalContainer.querySelector(
                ".bio-image-panel-image",
            );
            this.modalDescription = this.modalContainer.querySelector(
                ".bio-image-panel-description",
            );
            this.closeButton = this.modalContainer.querySelector(
                ".bio-image-panel-close",
            );

            // Re-bind event listeners
            this.bindEvents();
        }
    }

    /**
     * Create the panel HTML structure
     */
    createPanelStructure() {
        console.log("Creating image panel structure");

        // Create panel container
        this.modalContainer = document.createElement("div");
        this.modalContainer.id = "bio-image-panel";
        this.modalContainer.className = "bio-image-panel";

        // Create panel content wrapper
        const modalContent = document.createElement("div");
        modalContent.className = "bio-image-panel-content";

        // Create image element
        this.modalImage = document.createElement("img");
        this.modalImage.className = "bio-image-panel-image";
        this.modalImage.alt = "Historical figure image";

        // Create description wrapper to hold description and send to chat button
        const descriptionWrapper = document.createElement("div");
        descriptionWrapper.className = "bio-image-panel-description-wrapper";

        // Create description element
        this.modalDescription = document.createElement("div");
        this.modalDescription.className = "bio-image-panel-description";

        // Create Send to Chat button
        this.sendToChatButton = document.createElement("button");
        this.sendToChatButton.className = "send-to-chat-button";
        this.sendToChatButton.textContent = "Send to Chat";
        this.sendToChatButton.id = "modal-send-to-chat";

        // Add description and button to the wrapper
        descriptionWrapper.appendChild(this.modalDescription);
        descriptionWrapper.appendChild(this.sendToChatButton);

        // Create close button
        this.closeButton = document.createElement("button");
        this.closeButton.className = "bio-image-panel-close";
        this.closeButton.innerHTML = "&times;";
        this.closeButton.setAttribute("aria-label", "Close image detail");

        // Construct panel structure
        modalContent.appendChild(this.closeButton);
        modalContent.appendChild(this.modalImage);
        modalContent.appendChild(descriptionWrapper);
        this.modalContainer.appendChild(modalContent);

        // Add panel to page
        document.body.appendChild(this.modalContainer);

        // Bind event listeners
        this.bindEvents();
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
        // Close panel when clicking close button
        this.closeButton.addEventListener("click", () => this.hidePanel());

        // Close panel when pressing escape key
        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape" && this.isVisible()) {
                this.hidePanel();
            }
        });

        // Send to Chat button functionality
        if (this.sendToChatButton) {
            this.sendToChatButton.addEventListener("click", () => {
                // Get image description
                const description = this.modalDescription.textContent;
                const imageAlt = this.modalImage.alt;

                // Close the image preview
                this.hidePanel();

                // Close bio sections
                const bioSections = document.querySelectorAll(".bio-section");
                bioSections.forEach((section) => {
                    section.style.display = "none";
                });

                // Show the chat interface
                const chatInterface = document.querySelector(".chat-interface");
                if (chatInterface) {
                    chatInterface.style.display = "block";
                }

                // Show the chat container
                const chatContainer = document.querySelector(".chat-container");
                if (chatContainer) {
                    chatContainer.classList.add("visible");
                }

                console.log("Speaking description directly via TTS:", description);

                // Use speakDirectly to send text directly to ElevenLabs TTS pipeline
                if (window.chatController && typeof window.chatController.speakDirectly === 'function') {
                    window.chatController.speakDirectly(description);
                } else {
                    console.warn("speakDirectly not available, falling back to sendMessage");
                    if (window.chatController) {
                        window.chatController.sendMessage(description, false, 'image_description');
                    }
                }
            });
        }
    }

    /**
     * Show the image in 3D space behind the model and directly send description to chat
     * @param {string} imageUrl - URL of the image to display
     * @param {string} description - Description text
     * @param {string} characterName - Name of the historical figure
     */
    showModal(imageUrl, description, characterName) {
        console.log(
            `Displaying image for ${characterName} behind model and sending to chat: ${imageUrl}`,
        );

        // Display the image in 3D space behind the model
        if (window.modelViewer) {
            console.log("Displaying image in 3D space behind the model");
            window.modelViewer
                .showImage3D(imageUrl, description)
                .then((success) => {
                    if (success) {
                        console.log("Image displayed successfully in 3D space");
                    } else {
                        console.warn("Failed to display image in 3D space");
                    }
                })
                .catch((error) => {
                    console.error("Error displaying image in 3D space:", error);
                });
        } else {
            console.warn("ModelViewer not available for 3D image display");
        }

        // Directly send the description to chat without showing the side panel
        this.sendDescriptionToChat(description);
    }

    /**
     * Send the image description directly to chat
     * @param {string} description - The image description to send
     */
    sendDescriptionToChat(description) {
        // Close the bio panel properly (like the bio button does)
        const bioPanel = document.getElementById("bio-panel");
        const bioButton = document.getElementById("bio-button");

        if (bioPanel) {
            // Remove active class from bio button if it exists
            if (bioButton) {
                bioButton.classList.remove("bio-button-active");
            }

            // Add smooth sliding animation when closing
            bioPanel.style.transition =
                "transform 0.9s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.7s ease, width 0.5s ease";

            // Apply sliding effect
            bioPanel.style.transform = "translateX(110%)";
            bioPanel.style.opacity = "0.2";

            // Remove active class after animation finishes
            setTimeout(() => {
                bioPanel.classList.remove("active");
                bioPanel.style.transform = "translateX(100%)";
            }, 900);
        }

        // Show the chat container
        const chatContainer = document.querySelector(".chat-container");
        if (chatContainer) {
            chatContainer.classList.add("visible");
        }

        console.log("Speaking description directly via TTS:", description);

        // Use speakDirectly to send text directly to ElevenLabs TTS pipeline
        // This bypasses AI response generation and speaks the description directly
        if (window.chatController && typeof window.chatController.speakDirectly === 'function') {
            window.chatController.speakDirectly(description);
        } else {
            console.warn("speakDirectly not available, falling back to sendMessage");
            if (window.chatController) {
                window.chatController.sendMessage(description, false, 'image_description');
            }
        }
    }

    /**
     * Hide the side panel and 3D image
     */
    hidePanel() {
        console.log("Hiding image panel");
        this.modalContainer.classList.remove("show");

        // Also hide the 3D image if ModelViewer is available
        if (window.modelViewer && window.modelViewer.hideImage3D) {
            console.log("Hiding 3D image display");
            window.modelViewer.hideImage3D();
        }
    }

    /**
     * Check if the panel is currently visible
     * @returns {boolean} Whether the panel is visible
     */
    isVisible() {
        return (
            this.modalContainer &&
            this.modalContainer.classList.contains("show")
        );
    }
}
